import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { GroupTalkReportModal } from "../components/GroupTalkReportModal";
import { SafeMarkdown } from "../components/SafeMarkdown";
import { getAppContentViewportHeightClass } from "../components/AppHeaderFrame";
import { useCurrentCharacterAvatar } from "../hooks/useCurrentCharacterAvatar";
import { useCurrentCharacterName } from "../hooks/useCurrentCharacterName";
import { usePreferredCallName } from "../hooks/usePreferredCallName";
import { useProductCatalog } from "../hooks/useProductCatalog";
import { api, type GroupTalkReportResponse } from "../lib/api";
import { AppError, getBackendErrorCode, logError, toUserMessage } from "../lib/errors";
import {
  buildGroupTalkParticipantImageMap,
  findGroupTalkMember,
  getGroupTalkFallbackImageSrc,
  getGroupTalkDisplayMode,
  getGroupTalkImageSrc,
  type GroupTalkDisplayMode,
  type GroupTalkTemplateId,
} from "../lib/groupTalk";
import {
  clearGroupTalkActiveSessionSnapshot,
  createGroupTalkClientRequestId,
  loadGroupTalkActiveSessionSnapshot,
  saveGroupTalkActiveSessionSnapshot,
  type GroupTalkActiveSessionSnapshot,
  type GroupTalkPendingOperation,
} from "../lib/groupTalkSessionStorage";
import {
  DEFAULT_GROUP_TALK_CHOICE_INPUT_PROMPT,
  GROUP_TALK_CHOICE_INPUT_MAX_LENGTH,
  buildGroupTalkInputChoiceMessage,
  getGroupTalkChoicePayloadId,
  normalizeGroupTalkIntakeView,
  normalizeGroupTalkSessionDetailView,
  normalizeGroupTalkSessionView,
  normalizeGroupTalkTurnView,
  type GroupTalkNextChoiceView,
  type GroupTalkSessionView,
  type GroupTalkTranscriptMessage,
  type GroupTalkTurnView,
} from "../lib/groupTalkView";
import {
  GROUP_TALK_REPORT_GENERAL_ERROR_MESSAGE,
  canRegenerateGroupTalkReport,
  getGroupTalkReportActionErrorMessage,
  hasGroupTalkSummaryMessage,
  isGroupTalkReportStale,
  markGroupTalkReportFresh,
} from "../lib/groupTalkReport";
import { getKnowledgeTypeLabel } from "../lib/knowledge";
import { countTrimmedUnicodeCodePoints, type ProductCatalog } from "../lib/productCatalog";

const EXPERT_MESSAGE_DELAY_MS = 2000;
const SUMMARY_MESSAGE_DELAY_MS = 1500;
const THINKING_MESSAGE_ROTATE_MS = 1800;
const DEFAULT_THINKING_MESSAGES = ["みんなで考えています...", "論点を整理しています..."];
const INTAKE_THINKING_MESSAGE = "整理しています...";
const INTAKE_THINKING_MESSAGE_ID_PREFIX = "pending_intake";

// 通信切断・タイムアウトでは、バックエンド側の処理が正常完了している可能性がある。
// このコードのときだけ「結果を確認しています」の再確認フローへ進む（業務エラーは対象外）。
const GROUP_TALK_RECONCILE_ELIGIBLE_ERROR_CODES = new Set(["NETWORK", "INVALID_RESPONSE", "TIMEOUT"]);
const GROUP_TALK_RECONCILE_POLL_DELAYS_MS = [4000, 8000, 12000];
const GROUP_TALK_SEND_UNCONFIRMED_MESSAGE =
  "処理状況を確認できませんでした。画面を再読み込みしてください。";
const GROUP_TALK_SESSION_SAFETY_ERROR_CODE = "group_talk_session_safety_limit";

type GroupTalkProductPolicy = ProductCatalog["capabilities"]["groupTalk"];

function buildGroupTalkOverLimitMessage(policy: GroupTalkProductPolicy): string {
  const limit = policy.userInputMaxCharacters.toLocaleString();
  if (!policy.knowledgeChangeDuringSession) {
    return `相談内容は${limit}文字までです。このルームでは途中から参考資料を追加できません。長い資料はPDFとしてナレッジライブラリへ登録し、新しいAI壁打ちルームを始めるときに${policy.knowledgeSelectionTiming.displayName}の参考資料として選んでください。`;
  }
  return `相談内容は${limit}文字までです。長い資料はPDFとしてナレッジライブラリへ登録し、${policy.knowledgeSelectionTiming.displayName}の参考資料として選んでください。`;
}

function isGroupTalkReconcileEligibleError(errorValue: unknown): errorValue is AppError {
  return errorValue instanceof AppError && GROUP_TALK_RECONCILE_ELIGIBLE_ERROR_CODES.has(errorValue.code);
}

// サーバーが確定させた「そのターンの最後のメッセージ」かどうか。
// intake止まりの正規終了・expert/summary到達のどちらも is_final_in_turn=true で表現される。
function isGroupTalkTurnResolved(turn: GroupTalkTurnView): boolean {
  const lastMessage = turn.messages[turn.messages.length - 1];
  return Boolean(lastMessage?.isFinalInTurn);
}

function findRecoveredGroupTalkTurn(
  turns: GroupTalkTurnView[],
  knownTurnIds: Set<string>,
  sentUserMessage: string,
  targetTurnId?: string
): GroupTalkTurnView | null {
  for (let index = turns.length - 1; index >= 0; index -= 1) {
    const turn = turns[index];
    if (targetTurnId ? turn.turnId !== targetTurnId : knownTurnIds.has(turn.turnId)) continue;
    const userMessage = turn.messages.find((message) => message.phase === "user");
    if (userMessage?.content === sentUserMessage) return turn;
  }
  return null;
}

type GroupTalkRecoveryOutcome =
  | { status: "recovered"; session: GroupTalkSessionView; turns: GroupTalkTurnView[] }
  | { status: "not_found" };

async function fetchGroupTalkRecoveryCandidate(
  sessionId: string,
  characterName: string,
  displayMode: GroupTalkDisplayMode,
  knownTurnIds: Set<string>,
  sentUserMessage: string,
  targetTurnId?: string
): Promise<GroupTalkRecoveryOutcome> {
  const response = await api.getGroupTalkSession(sessionId);
  const normalized = normalizeGroupTalkSessionDetailView(response, characterName, displayMode);
  const recoveredTurn = findRecoveredGroupTalkTurn(normalized.turns, knownTurnIds, sentUserMessage, targetTurnId);

  if (recoveredTurn && isGroupTalkTurnResolved(recoveredTurn)) {
    return { status: "recovered", session: normalized.session, turns: normalized.turns };
  }

  return { status: "not_found" };
}

async function pollForGroupTalkRecovery(
  sessionId: string,
  characterName: string,
  displayMode: GroupTalkDisplayMode,
  knownTurnIds: Set<string>,
  sentUserMessage: string,
  targetTurnId?: string
): Promise<GroupTalkRecoveryOutcome> {
  for (let attempt = 0; attempt <= GROUP_TALK_RECONCILE_POLL_DELAYS_MS.length; attempt += 1) {
    if (attempt > 0) {
      await wait(GROUP_TALK_RECONCILE_POLL_DELAYS_MS[attempt - 1]);
    }
    try {
      const outcome = await fetchGroupTalkRecoveryCandidate(
        sessionId,
        characterName,
        displayMode,
        knownTurnIds,
        sentUserMessage,
        targetTurnId
      );
      if (outcome.status === "recovered") return outcome;
    } catch (errorValue) {
      // 再確認の1回分がネットワークエラー等で失敗しても、確認自体を諦めず残りのポーリングを続ける。
      // ただしセッション切れは即座に呼び出し元へ伝える。
      if (errorValue instanceof AppError && errorValue.code === "SESSION_EXPIRED") {
        throw errorValue;
      }
      logError(errorValue);
    }
  }
  return { status: "not_found" };
}

function buildThinkingMessages(members: Array<{ memberId: string }>) {
  const memberIds = new Set(members.map((member) => member.memberId));
  const messages = ["みんなで考えています..."];

  if (memberIds.has("cfo") || memberIds.has("tax_accountant") || memberIds.has("tax")) {
    messages.push("数字面を整理しています...");
  }

  if (
    memberIds.has("lawyer") ||
    memberIds.has("social_insurance_advisor") ||
    memberIds.has("law") ||
    memberIds.has("labor")
  ) {
    messages.push("リスクや制度面を確認しています...");
  }

  if (memberIds.has("cmo") || memberIds.has("customer")) {
    messages.push("市場やユーザー目線を整理しています...");
  }

  if (memberIds.has("cto") || memberIds.has("pm") || memberIds.has("coo") || memberIds.has("consultant")) {
    messages.push("進め方をすり合わせています...");
  }

  return messages.length > 0 ? messages : DEFAULT_THINKING_MESSAGES;
}

function createThinkingMessage(text = DEFAULT_THINKING_MESSAGES[0], createdAt = new Date().toISOString()): GroupTalkTranscriptMessage {
  return {
    messageId: `pending_meeting_${Date.now()}`,
    participantId: null,
    memberId: null,
    speakerId: "meeting",
    speakerName: "会議メンバー",
    speakerType: "facilitator",
    roleLabel: "ファシリテータ",
    stanceId: null,
    stanceLabel: null,
    basicStanceLabel: null,
    facilitator: true,
    phase: "thinking",
    sequence: null,
    isFinalInTurn: false,
    content: text,
    nextChoices: [],
    createdAt,
  };
}

function createIntakeThinkingMessage(createdAt = new Date().toISOString()): GroupTalkTranscriptMessage {
  return {
    ...createThinkingMessage(INTAKE_THINKING_MESSAGE, createdAt),
    messageId: `${INTAKE_THINKING_MESSAGE_ID_PREFIX}_${Date.now()}`,
    speakerName: "日向 メイ",
  };
}

function isIntakeThinkingMessage(message: GroupTalkTranscriptMessage) {
  return message.messageId.startsWith(`${INTAKE_THINKING_MESSAGE_ID_PREFIX}_`);
}

function createPendingTurn(userMessage: string, speakerName: string): GroupTalkTurnView {
  const createdAt = new Date().toISOString();
  const userRow: GroupTalkTranscriptMessage = {
    messageId: `pending_user_${Date.now()}`,
    participantId: null,
    memberId: null,
    speakerId: "user",
    speakerName,
    speakerType: "user",
    roleLabel: "ユーザー",
    stanceId: null,
    stanceLabel: null,
    basicStanceLabel: null,
    facilitator: false,
    phase: "user",
    sequence: 0,
    isFinalInTurn: false,
    content: userMessage,
    nextChoices: [],
    createdAt,
  };

  return {
    sessionId: "",
    turnId: `pending_turn_${Date.now()}`,
    messages: [userRow, createIntakeThinkingMessage(createdAt)],
    createdAt,
    isSatisfied: false,
  };
}

function buildMessageSpeakerLabel(
  message: GroupTalkTranscriptMessage,
  characterName: string,
  preferredCallName: string
) {
  if (message.speakerType === "user") return preferredCallName;

  const speakerName = message.facilitator ? characterName : message.speakerName;
  const stanceLabel = !message.facilitator ? message.basicStanceLabel ?? message.stanceLabel : null;
  const roleWithStance = stanceLabel ? `${message.roleLabel}（${stanceLabel}）` : message.roleLabel;
  return `${speakerName} / ${roleWithStance}`;
}

function wait(ms: number) {
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function splitMessagesForReveal(messages: GroupTalkTranscriptMessage[]) {
  const userMessages: GroupTalkTranscriptMessage[] = [];
  const intakeMessages: GroupTalkTranscriptMessage[] = [];
  const expertMessages: GroupTalkTranscriptMessage[] = [];
  const summaryMessages: GroupTalkTranscriptMessage[] = [];

  messages.forEach((message) => {
    if (message.phase === "user") {
      userMessages.push(message);
      return;
    }
    if (message.phase === "intake") {
      intakeMessages.push(message);
      return;
    }
    if (message.phase === "summary") {
      summaryMessages.push(message);
      return;
    }
    if (message.phase === "facilitator_direct") {
      summaryMessages.push(message);
      return;
    }
    if (message.phase === "expert") {
      expertMessages.push(message);
    }
  });

  return {
    initialMessages: [...userMessages, ...intakeMessages],
    expertMessages,
    summaryMessages,
  };
}

function mergeTurnMessages(intakeMessages: GroupTalkTranscriptMessage[], completionMessages: GroupTalkTranscriptMessage[]) {
  const completionBody = completionMessages
    .filter((message) => message.phase !== "user" && message.phase !== "intake")
    .sort((left, right) => {
      if (left.sequence === null && right.sequence === null) return 0;
      if (left.sequence === null) return 1;
      if (right.sequence === null) return -1;
      return left.sequence - right.sequence;
    });

  return [...intakeMessages, ...completionBody];
}

function findLatestChoiceMessageId(turns: GroupTalkTurnView[]) {
  const latestTurn = turns[turns.length - 1];
  if (!latestTurn) return null;

  for (let index = latestTurn.messages.length - 1; index >= 0; index -= 1) {
    const message = latestTurn.messages[index];
    if (message.phase === "thinking") continue;
    return (message.nextChoices ?? []).length > 0 ? message.messageId : null;
  }

  return null;
}

type SelectedChoiceState = {
  messageId: string;
  value: string;
  label: string;
};

type ChoiceInputState = {
  messageId: string;
  sourcePhase: GroupTalkTranscriptMessage["phase"];
  choice: GroupTalkNextChoiceView;
  value: string;
  error: string;
};

type SelectedChoiceContext = {
  messageId: string;
  sourcePhase: GroupTalkTranscriptMessage["phase"];
  choice: GroupTalkNextChoiceView;
};

type SendUserMessageOptions = {
  pendingOperation?: GroupTalkPendingOperation;
};

function getChoiceInputPrompt(choice: GroupTalkNextChoiceView) {
  return choice.inputPrompt || DEFAULT_GROUP_TALK_CHOICE_INPUT_PROMPT;
}

function buildSelectedChoicePayload(context?: SelectedChoiceContext) {
  if (!context) return undefined;

  return {
    choiceId: getGroupTalkChoicePayloadId(context.choice),
    choiceType: context.choice.choiceType,
    actionIntent: context.choice.actionIntent,
    sourcePhase: context.choice.sourcePhase || context.sourcePhase,
    sourceMessageId: context.choice.sourceMessageId || context.messageId,
  };
}

export default function GroupTalkSession() {
  const { sessionId = "" } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const characterName = useCurrentCharacterName();
  const characterAvatarSrc = useCurrentCharacterAvatar();
  const preferredCallName = usePreferredCallName();
  const { catalog, error: catalogError, retry: retryCatalog } = useProductCatalog();
  const groupTalkPolicy = catalog?.capabilities.groupTalk ?? null;
  const endRef = useRef<HTMLDivElement | null>(null);
  const composerRef = useRef<HTMLDivElement | null>(null);
  const initialComposerScrollDoneRef = useRef(false);
  const initialUserMessageSendKeyRef = useRef<string | null>(null);
  const sendingRef = useRef(false);
  const pendingOperationAttemptRef = useRef<string | null>(null);
  const completingTurnRef = useRef<string | null>(null);
  const mountedRef = useRef(true);

  const [snapshot, setSnapshot] = useState<GroupTalkActiveSessionSnapshot | null>(() =>
    sessionId ? loadGroupTalkActiveSessionSnapshot(sessionId) : null
  );
  const [loadingSession, setLoadingSession] = useState(() =>
    sessionId ? !loadGroupTalkActiveSessionSnapshot(sessionId) : false
  );
  const [composerText, setComposerText] = useState("");
  const [sending, setSending] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [sessionSafetyReached, setSessionSafetyReached] = useState(false);
  const [pendingTurn, setPendingTurn] = useState<GroupTalkTurnView | null>(null);
  const [thinkingMessageIndex, setThinkingMessageIndex] = useState(0);
  const [selectedChoice, setSelectedChoice] = useState<SelectedChoiceState | null>(null);
  const [activeChoiceInput, setActiveChoiceInput] = useState<ChoiceInputState | null>(null);
  const [report, setReport] = useState<GroupTalkReportResponse | null>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportCreating, setReportCreating] = useState(false);
  const [reportError, setReportError] = useState("");
  const [reportModalOpen, setReportModalOpen] = useState(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (snapshot) {
      saveGroupTalkActiveSessionSnapshot(snapshot);
    }
  }, [snapshot]);

  const thinkingMessages = useMemo(
    () => (snapshot ? buildThinkingMessages(snapshot.session.members) : DEFAULT_THINKING_MESSAGES),
    [snapshot]
  );
  const displayMode = useMemo(() => getGroupTalkDisplayMode(snapshot?.draft.mode ?? snapshot?.draft.templateId), [snapshot]);

  const loadReport = useCallback(async (options?: { silent?: boolean }) => {
    if (!sessionId) return;

    const silent = options?.silent === true;
    if (!silent) {
      setReportLoading(true);
      setReportError("");
    }

    try {
      const response = await api.getGroupTalkSessionReport(sessionId);
      setReport(response);
      setReportError("");
    } catch (errorValue) {
      if (errorValue instanceof AppError && errorValue.code === "SESSION_EXPIRED") {
        navigate("/splash", { replace: true });
        return;
      }

      logError(errorValue);
      if (!silent) {
        setReportError(GROUP_TALK_REPORT_GENERAL_ERROR_MESSAGE);
      }
    } finally {
      if (!silent) {
        setReportLoading(false);
      }
    }
  }, [navigate, sessionId]);

  useEffect(() => {
    void loadReport();
  }, [loadReport]);

  useEffect(() => {
    if (!sessionId) {
      navigate("/talk/group", {
        replace: true,
        state: { errorMessage: "会議セッションが見つかりませんでした。設定画面からやり直してください。" },
      });
      return;
    }

    const loaded = loadGroupTalkActiveSessionSnapshot(sessionId);
    if (loaded) {
      setSnapshot(loaded);
      setLoadingSession(false);

      // キャッシュ表示はそのまま維持しつつ、バックグラウンドでサーバーの最新状態を確認する。
      // 前回セッション中に通信切断・タイムアウトで再確認が完了しないままリロードされた場合、
      // キャッシュには未完了のターンしか残っていないことがあるため。
      let stillActive = true;
      void (async () => {
        try {
          const response = await api.getGroupTalkSession(sessionId);
          if (!stillActive) return;

          const normalized = normalizeGroupTalkSessionDetailView(response, characterName, displayMode);
          const lastCachedTurn = loaded.turns[loaded.turns.length - 1];
          const isLastCachedTurnUnresolved = Boolean(lastCachedTurn) && !isGroupTalkTurnResolved(lastCachedTurn);
          const hasNewTurnsOnServer = normalized.turns.length > loaded.turns.length;
          if (!isLastCachedTurnUnresolved && !hasNewTurnsOnServer) return;

          setSnapshot((current) => {
            if (!current || current.sessionId !== sessionId) return current;
            // pending中のturnは専用recoveryが確定させる。背景読戻しでoptimistic表示と二重にしない。
            if (current.pendingOperation) return current;
            const nextSnapshot: GroupTalkActiveSessionSnapshot = {
              ...current,
              session: normalized.session,
              turns: normalized.turns,
            };
            saveGroupTalkActiveSessionSnapshot(nextSnapshot);
            return nextSnapshot;
          });
        } catch (errorValue) {
          // バックグラウンド再確認の失敗はキャッシュ表示のまま継続する（ユーザーへエラー表示しない）
          logError(errorValue);
        }
      })();

      return () => {
        stillActive = false;
      };
    }

    let active = true;
    setLoadingSession(true);
    setLoadError("");

    (async () => {
      try {
        const response = await api.getGroupTalkSession(sessionId);
        if (!active) return;

        const normalized = normalizeGroupTalkSessionDetailView(
          response,
          characterName,
          getGroupTalkDisplayMode(response.template_id === "custom" ? "custom" : "named")
        );
        const nextSnapshot: GroupTalkActiveSessionSnapshot = {
          sessionId: normalized.session.sessionId,
          draft: {
            templateId: ((normalized.session.templateId as GroupTalkTemplateId | null) ?? "custom"),
            title: normalized.session.title,
            description: normalized.session.description,
            participants: normalized.session.members.map((member) => ({
              participantId: member.participantId,
              memberId: member.memberId,
              stanceId: member.stanceId,
              fixed: Boolean(findGroupTalkMember(member.memberId).fixed),
              displayName: member.label,
              personaKey: member.personaKey,
              avatarKey: member.avatarKey,
              imageSrc: member.imageSrc,
            })),
          },
          session: normalized.session,
          turns: normalized.turns,
        };

        saveGroupTalkActiveSessionSnapshot(nextSnapshot);
        setSnapshot(nextSnapshot);
      } catch (errorValue) {
        if (!active) return;

        if (errorValue instanceof AppError && errorValue.code === "SESSION_EXPIRED") {
          navigate("/splash", { replace: true });
          return;
        }

        logError(errorValue);
        navigate("/talk/group", {
          replace: true,
          state: { errorMessage: "セッションを復元できませんでした。続きから始める一覧で再度お試しください。" },
        });
      } finally {
        if (active) {
          setLoadingSession(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [characterName, displayMode, navigate, sessionId]);

  useEffect(() => {
    initialComposerScrollDoneRef.current = false;
  }, [sessionId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [pendingTurn]);

  useEffect(() => {
    if (!snapshot || snapshot.turns.length === 0 || initialComposerScrollDoneRef.current) {
      return;
    }

    initialComposerScrollDoneRef.current = true;
    window.setTimeout(() => {
      composerRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }, 0);
  }, [snapshot]);

  useEffect(() => {
    const hasThinkingMessage = pendingTurn?.messages.some(
      (message) => message.phase === "thinking" && !isIntakeThinkingMessage(message)
    );
    if (!hasThinkingMessage) {
      setThinkingMessageIndex(0);
      return;
    }

    const timer = window.setInterval(() => {
      setThinkingMessageIndex((current) => (current + 1) % thinkingMessages.length);
    }, THINKING_MESSAGE_ROTATE_MS);

    return () => {
      window.clearInterval(timer);
    };
  }, [pendingTurn, thinkingMessages]);

  useEffect(() => {
    setPendingTurn((current) => {
      if (!current) return current;
      const hasThinkingMessage = current.messages.some((message) => message.phase === "thinking");
      if (!hasThinkingMessage) return current;

      return {
        ...current,
        messages: current.messages.map((message) =>
          message.phase === "thinking" && !isIntakeThinkingMessage(message)
            ? { ...message, content: thinkingMessages[thinkingMessageIndex] }
            : message
        ),
      };
    });
  }, [thinkingMessageIndex, thinkingMessages]);

  const allTurns = useMemo(() => {
    if (!snapshot) return pendingTurn ? [pendingTurn] : [];
    return pendingTurn ? [...snapshot.turns, pendingTurn] : snapshot.turns;
  }, [pendingTurn, snapshot]);
  const operationPending = Boolean(snapshot?.pendingOperation);
  const composerCharacterCount = countTrimmedUnicodeCodePoints(composerText);
  const composerOverLimit = groupTalkPolicy !== null
    && composerCharacterCount > groupTalkPolicy.userInputMaxCharacters;

  const latestChoiceMessageId = useMemo(() => findLatestChoiceMessageId(allTurns), [allTurns]);

  const hasActiveChoices = latestChoiceMessageId !== null
    && !sending && !sessionSafetyReached && !operationPending
    && selectedChoice?.messageId !== latestChoiceMessageId
    && activeChoiceInput?.messageId !== latestChoiceMessageId;

  const participantImageMap = useMemo(
    () => (snapshot ? buildGroupTalkParticipantImageMap(snapshot.session.members, displayMode) : new Map<string, string>()),
    [displayMode, snapshot]
  );

  const sendUserMessage = useCallback(async (
    messageText: string,
    choice?: SelectedChoiceContext,
    options?: SendUserMessageOptions
  ): Promise<boolean> => {
    const trimmed = messageText.trim();
    if (!snapshot || !trimmed || sendingRef.current || sessionSafetyReached) return false;
    if (
      !options?.pendingOperation
      && groupTalkPolicy
      && countTrimmedUnicodeCodePoints(trimmed) > groupTalkPolicy.userInputMaxCharacters
    ) {
      setLoadError(buildGroupTalkOverLimitMessage(groupTalkPolicy));
      return false;
    }

    const recoveredOperation = options?.pendingOperation;
    if (
      recoveredOperation &&
      (recoveredOperation.sessionId !== snapshot.session.sessionId || recoveredOperation.userMessage !== trimmed)
    ) {
      setLoadError("保存された送信情報が現在のセッションと一致しません。画面を再読み込みしてください。");
      return false;
    }
    if (!recoveredOperation && snapshot.pendingOperation) {
      setLoadError(GROUP_TALK_SEND_UNCONFIRMED_MESSAGE);
      return false;
    }

    sendingRef.current = true;
    const selectedChoicePayload = recoveredOperation?.selectedChoice ?? buildSelectedChoicePayload(choice);
    const operation: GroupTalkPendingOperation = recoveredOperation ?? {
      sessionId: snapshot.session.sessionId,
      clientRequestId: createGroupTalkClientRequestId(),
      requestType: "intake",
      userMessage: trimmed,
      ...(selectedChoicePayload ? { selectedChoice: selectedChoicePayload } : {}),
      startedAt: new Date().toISOString(),
    };
    const operationSnapshot: GroupTalkActiveSessionSnapshot = {
      ...snapshot,
      pendingOperation: operation,
    };
    let activeOperation = operation;
    let activeOperationSnapshot = operationSnapshot;
    pendingOperationAttemptRef.current = `${operation.sessionId}:${operation.clientRequestId}`;

    // APIを開始する前に永続化し、remount/reload後も同じ操作IDへ収束させる。
    saveGroupTalkActiveSessionSnapshot(operationSnapshot);
    setSnapshot(operationSnapshot);

    if (choice) {
      setSelectedChoice({
        messageId: choice.messageId,
        value: choice.choice.value,
        label: choice.choice.label,
      });
    }

    setSending(true);
    setLoadError("");
    setComposerText("");
    const createdPendingTurn = createPendingTurn(trimmed, preferredCallName);
    setPendingTurn(createdPendingTurn);

    try {
      if (operation.completionTurnId) {
        // /complete はbackend idempotency未対応のため、remount後は再POSTせず完成済み結果を読み戻す。
        setRecovering(true);
        const outcome = await pollForGroupTalkRecovery(
          operation.sessionId,
          characterName,
          displayMode,
          new Set(operationSnapshot.turns.map((turn) => turn.turnId)),
          trimmed,
          operation.completionTurnId
        );
        if (outcome.status === "recovered") {
          const nextSnapshot: GroupTalkActiveSessionSnapshot = {
            ...operationSnapshot,
            session: outcome.session,
            turns: outcome.turns,
            pendingOperation: undefined,
          };
          saveGroupTalkActiveSessionSnapshot(nextSnapshot);
          setSnapshot(nextSnapshot);
          setPendingTurn(null);
          setLoadError("");
          return true;
        }

        setLoadError(GROUP_TALK_SEND_UNCONFIRMED_MESSAGE);
        return false;
      }

      const intakeResponse = await api.createGroupTalkIntake(operation.sessionId, {
        userMessage: trimmed,
        clientRequestId: operation.clientRequestId,
        selectedChoice: selectedChoicePayload,
      });
      if (!mountedRef.current) return false;

      const nextSession = normalizeGroupTalkSessionView(
        {
          ...operationSnapshot.session,
          session_id: intakeResponse.session_id,
          template_id: operationSnapshot.session.templateId,
          title: operationSnapshot.session.title,
          description: operationSnapshot.session.description,
          members: operationSnapshot.session.members.map((member) => ({
            participant_id: member.participantId,
            member_id: member.memberId,
            speaker_id: member.speakerId,
            display_name: member.label,
            persona_key: member.personaKey,
            avatar_key: member.avatarKey,
            avatar_url: member.imageSrc,
            role: member.role,
            stance: member.stanceId,
          })),
          agreed_notes: intakeResponse.agreed_notes,
          session_state: intakeResponse.session_state,
          status: operationSnapshot.session.status,
          created_at: operationSnapshot.session.createdAt,
        },
        characterName,
        displayMode
      );

      const intakeTurn = normalizeGroupTalkIntakeView(intakeResponse, nextSession, characterName, displayMode);
      const fallbackUserMessages = createdPendingTurn.messages.filter((message) => message.phase === "user");
      const intakeMessages = intakeTurn.messages.length > 0 ? intakeTurn.messages : fallbackUserMessages;

      setPendingTurn({
        sessionId: intakeTurn.sessionId,
        turnId: intakeTurn.turnId,
        messages:
          intakeTurn.needsFollowUp || !intakeTurn.continueToken
            ? intakeMessages
            : [...intakeMessages, createThinkingMessage(thinkingMessages[Math.min(1, thinkingMessages.length - 1)])],
        createdAt: intakeTurn.createdAt,
        isSatisfied: false,
      });

      if (intakeTurn.needsFollowUp || !intakeTurn.continueToken) {
        const nextTurn: GroupTalkTurnView = {
          sessionId: intakeTurn.sessionId,
          turnId: intakeTurn.turnId,
          messages: intakeMessages,
          createdAt: intakeTurn.createdAt,
          isSatisfied: false,
        };

        const nextSnapshot: GroupTalkActiveSessionSnapshot = {
          ...operationSnapshot,
          session: {
            ...nextSession,
            agreedNotes: intakeTurn.agreedNotes,
            sessionState: intakeTurn.sessionState,
          },
          turns: [...operationSnapshot.turns, nextTurn],
          pendingOperation: undefined,
        };

        saveGroupTalkActiveSessionSnapshot(nextSnapshot);
        setSnapshot(nextSnapshot);
        setPendingTurn(null);
        return true;
      }

      const completingOperation: GroupTalkPendingOperation = {
        ...operation,
        completionTurnId: intakeTurn.turnId,
        completionStartedAt: new Date().toISOString(),
      };
      const completingSnapshot: GroupTalkActiveSessionSnapshot = {
        ...operationSnapshot,
        pendingOperation: completingOperation,
      };
      saveGroupTalkActiveSessionSnapshot(completingSnapshot);
      setSnapshot(completingSnapshot);
      activeOperation = completingOperation;
      activeOperationSnapshot = completingSnapshot;

      if (completingTurnRef.current) return false;
      completingTurnRef.current = intakeTurn.turnId;
      const completionResponse = await api.createGroupTalkTurnComplete(operation.sessionId, intakeTurn.turnId, {
        continueToken: intakeTurn.continueToken,
      });
      if (!mountedRef.current) return false;

      const completedSession = normalizeGroupTalkSessionView(
        {
          ...nextSession,
          session_id: completionResponse.session_id,
          template_id: nextSession.templateId,
          title: nextSession.title,
          description: nextSession.description,
          members: nextSession.members.map((member) => ({
            participant_id: member.participantId,
            member_id: member.memberId,
            speaker_id: member.speakerId,
            display_name: member.label,
            persona_key: member.personaKey,
            avatar_key: member.avatarKey,
            avatar_url: member.imageSrc,
            role: member.role,
            stance: member.stanceId,
          })),
          agreed_notes: completionResponse.agreed_notes,
          session_state: completionResponse.session_state,
          status: nextSession.status,
          created_at: nextSession.createdAt,
        },
        characterName,
        displayMode
      );

      const completedTurn = normalizeGroupTalkTurnView(completionResponse, completedSession, characterName, displayMode);
      const mergedMessages = mergeTurnMessages(intakeMessages, completedTurn.messages);
      const { initialMessages, expertMessages, summaryMessages } = splitMessagesForReveal(mergedMessages);
      const revealedInitialMessages = initialMessages.length > 0 ? initialMessages : fallbackUserMessages;
      const reportShouldRefreshAfterTurn =
        report?.status === "completed" || report?.status === "failed" || report?.status === "generating";
      const completedReportShouldBecomeStale =
        report?.status === "completed" && (expertMessages.length > 0 || summaryMessages.length > 0);

      setPendingTurn({
        ...completedTurn,
        messages: revealedInitialMessages,
      });

      for (const message of expertMessages) {
        await wait(EXPERT_MESSAGE_DELAY_MS);
        setPendingTurn((current) => {
          if (!current || current.turnId !== completedTurn.turnId) return current;
          return {
            ...current,
            messages: [...current.messages, message],
          };
        });
      }

      for (const message of summaryMessages) {
        await wait(SUMMARY_MESSAGE_DELAY_MS);
        setPendingTurn((current) => {
          if (!current || current.turnId !== completedTurn.turnId) return current;
          return {
            ...current,
            messages: [...current.messages, message],
          };
        });
      }

      if (!mountedRef.current) return false;

      const nextSnapshot: GroupTalkActiveSessionSnapshot = {
        ...completingSnapshot,
        session: completedSession,
        turns: [
          ...completingSnapshot.turns,
          {
            ...completedTurn,
            messages: mergedMessages,
          },
        ],
        pendingOperation: undefined,
      };

      saveGroupTalkActiveSessionSnapshot(nextSnapshot);
      setSnapshot(nextSnapshot);
      setPendingTurn(null);
      if (completedReportShouldBecomeStale) {
        setReport((current) =>
          current?.status === "completed"
            ? {
                ...current,
                is_stale: true,
                can_regenerate: true,
              }
            : current
        );
      }
      if (reportShouldRefreshAfterTurn) {
        void loadReport({ silent: true });
      }
      return true;
    } catch (errorValue) {
      logError(errorValue);
      const safetyReached = getBackendErrorCode(errorValue) === GROUP_TALK_SESSION_SAFETY_ERROR_CODE;
      if (safetyReached) {
        setSessionSafetyReached(true);
      }
      const shouldRetainPending = isGroupTalkReconcileEligibleError(errorValue);
      if (!shouldRetainPending) {
        setPendingTurn(null);
      }

      if (errorValue instanceof AppError && errorValue.code === "SESSION_EXPIRED") {
        clearGroupTalkActiveSessionSnapshot(activeOperation.sessionId);
        navigate("/splash", { replace: true });
        return false;
      }

      // 通信切断・タイムアウトはバックエンド側で処理が完了している可能性があるため、
      // 「失敗」と決めつける前にセッションを再取得してターンの完了状態を確認する。
      if (shouldRetainPending) {
        setRecovering(true);
        try {
          const knownTurnIds = new Set(activeOperationSnapshot.turns.map((turn) => turn.turnId));
          const outcome = await pollForGroupTalkRecovery(
            activeOperation.sessionId,
            characterName,
            displayMode,
            knownTurnIds,
            trimmed,
            activeOperation.completionTurnId
          );

          if (outcome.status === "recovered") {
            const nextSnapshot: GroupTalkActiveSessionSnapshot = {
              ...activeOperationSnapshot,
              session: outcome.session,
              turns: outcome.turns,
              pendingOperation: undefined,
            };
            saveGroupTalkActiveSessionSnapshot(nextSnapshot);
            setSnapshot(nextSnapshot);
            setPendingTurn(null);
            setLoadError("");
            return true;
          }
        } catch (reconcileError) {
          logError(reconcileError);
          if (reconcileError instanceof AppError && reconcileError.code === "SESSION_EXPIRED") {
            clearGroupTalkActiveSessionSnapshot(activeOperation.sessionId);
            navigate("/splash", { replace: true });
            return false;
          }
        } finally {
          setRecovering(false);
        }
      }

      if (!shouldRetainPending) {
        const clearedSnapshot: GroupTalkActiveSessionSnapshot = {
          ...activeOperationSnapshot,
          pendingOperation: undefined,
        };
        saveGroupTalkActiveSessionSnapshot(clearedSnapshot);
        setSnapshot(clearedSnapshot);

        if (!choice) {
          setComposerText(trimmed);
        }
        if (choice) {
          setSelectedChoice((current) => (current?.messageId === choice.messageId ? null : current));
        }
      }

      setLoadError(shouldRetainPending ? GROUP_TALK_SEND_UNCONFIRMED_MESSAGE : toUserMessage(errorValue));
      return false;
    } finally {
      completingTurnRef.current = null;
      sendingRef.current = false;
      if (mountedRef.current) {
        setSending(false);
        setRecovering(false);
      }
    }
  }, [characterName, displayMode, groupTalkPolicy, loadReport, navigate, preferredCallName, report, sessionSafetyReached, snapshot, thinkingMessages]);

  useEffect(() => {
    const pendingOperation = snapshot?.pendingOperation;
    if (!snapshot || !pendingOperation || sending || pendingTurn) return;

    const operationKey = `${pendingOperation.sessionId}:${pendingOperation.clientRequestId}`;
    if (pendingOperationAttemptRef.current === operationKey) return;
    pendingOperationAttemptRef.current = operationKey;

    void (async () => {
      const succeeded = await sendUserMessage(pendingOperation.userMessage, undefined, { pendingOperation });
      setSnapshot((current) => {
        if (
          !current ||
          current.sessionId !== pendingOperation.sessionId ||
          current.initialUserMessage?.trim() !== pendingOperation.userMessage
        ) {
          return current;
        }
        // 通信結果不明ならpendingを維持し、reload時に同じIDで再確認する。
        if (!succeeded && current.pendingOperation) return current;

        const nextSnapshot: GroupTalkActiveSessionSnapshot = {
          ...current,
          initialUserMessage: undefined,
          initialUserMessageSent: succeeded,
        };
        saveGroupTalkActiveSessionSnapshot(nextSnapshot);
        return nextSnapshot;
      });
    })();
  }, [pendingTurn, sending, sendUserMessage, snapshot]);

  useEffect(() => {
    if (!snapshot || snapshot.pendingOperation || sending || pendingTurn) return;
    const initialUserMessage = snapshot.initialUserMessage?.trim();
    if (!initialUserMessage || snapshot.initialUserMessageSent) return;

    const sendKey = `${snapshot.sessionId}:${initialUserMessage}`;
    if (initialUserMessageSendKeyRef.current === sendKey) return;
    initialUserMessageSendKeyRef.current = sendKey;

    void (async () => {
      const succeeded = await sendUserMessage(initialUserMessage);
      setSnapshot((current) => {
        if (!current || current.sessionId !== snapshot.sessionId) return current;
        const nextSnapshot: GroupTalkActiveSessionSnapshot = {
          ...current,
          initialUserMessage: undefined,
          initialUserMessageSent: succeeded,
        };
        saveGroupTalkActiveSessionSnapshot(nextSnapshot);
        return nextSnapshot;
      });
      if (!succeeded) {
        initialUserMessageSendKeyRef.current = null;
      }
    })();
  }, [pendingTurn, sending, snapshot, sendUserMessage]);

  const handleSend = async () => {
    await sendUserMessage(composerText);
  };

  const handleGenerateReport = async () => {
    if (!sessionId || reportCreating || report?.status === "generating") return;

    setReportCreating(true);
    setReportError("");

    try {
      const response = await api.generateGroupTalkSessionReport(sessionId);
      setReport(markGroupTalkReportFresh(response));
    } catch (errorValue) {
      if (errorValue instanceof AppError && errorValue.code === "SESSION_EXPIRED") {
        navigate("/splash", { replace: true });
        return;
      }

      if (errorValue instanceof AppError && errorValue.meta.status === 409) {
        setReport({ status: "generating" });
      }
      if (!(errorValue instanceof AppError) || (errorValue.meta.status !== 409 && errorValue.meta.status !== 422)) {
        logError(errorValue);
      }
      setReportError(getGroupTalkReportActionErrorMessage(errorValue));
    } finally {
      setReportCreating(false);
    }
  };

  const handleChoiceSelect = async (message: GroupTalkTranscriptMessage, choice: GroupTalkNextChoiceView) => {
    if (
      sendingRef.current ||
      sessionSafetyReached ||
      operationPending ||
      selectedChoice?.messageId === message.messageId ||
      activeChoiceInput?.messageId === message.messageId ||
      message.messageId !== latestChoiceMessageId ||
      choice.choiceType === "finish"
    ) {
      return;
    }

    if (choice.requiresInput) {
      setLoadError("");
      setActiveChoiceInput({
        messageId: message.messageId,
        sourcePhase: message.phase,
        choice,
        value: "",
        error: "",
      });
      return;
    }

    await sendUserMessage(choice.label, { messageId: message.messageId, sourcePhase: message.phase, choice });
  };

  const handleChoiceInputChange = (messageId: string, value: string) => {
    setActiveChoiceInput((current) =>
      current?.messageId === messageId
        ? {
            ...current,
            value,
            error:
              value.length > GROUP_TALK_CHOICE_INPUT_MAX_LENGTH
                ? `入力が長すぎます。${GROUP_TALK_CHOICE_INPUT_MAX_LENGTH}文字以内で入力してください。`
                : "",
          }
        : current
    );
  };

  const handleChoiceInputCancel = (messageId: string) => {
    setActiveChoiceInput((current) => (current?.messageId === messageId ? null : current));
  };

  const handleChoiceInputSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (
      !activeChoiceInput ||
      sendingRef.current ||
      sessionSafetyReached ||
      operationPending ||
      activeChoiceInput.messageId !== latestChoiceMessageId
    ) return;

    const trimmedValue = activeChoiceInput.value.trim();
    if (!trimmedValue) {
      setActiveChoiceInput((current) =>
        current?.messageId === activeChoiceInput.messageId ? { ...current, error: "追加情報を入力してください。" } : current
      );
      return;
    }

    if (trimmedValue.length > GROUP_TALK_CHOICE_INPUT_MAX_LENGTH) {
      setActiveChoiceInput((current) =>
        current?.messageId === activeChoiceInput.messageId
          ? {
              ...current,
              error: `入力が長すぎます。${GROUP_TALK_CHOICE_INPUT_MAX_LENGTH}文字以内で入力してください。`,
            }
          : current
      );
      return;
    }

    const userMessage = buildGroupTalkInputChoiceMessage(activeChoiceInput.choice, trimmedValue);
    if (
      groupTalkPolicy
      && countTrimmedUnicodeCodePoints(userMessage) > groupTalkPolicy.userInputMaxCharacters
    ) {
      setActiveChoiceInput((current) =>
        current?.messageId === activeChoiceInput.messageId
          ? { ...current, error: "送信文が長すぎます。入力内容を短くしてください。" }
          : current
      );
      return;
    }

    const succeeded = await sendUserMessage(userMessage, {
      messageId: activeChoiceInput.messageId,
      sourcePhase: activeChoiceInput.sourcePhase,
      choice: activeChoiceInput.choice,
    });
    if (succeeded) {
      setActiveChoiceInput((current) =>
        current?.messageId === activeChoiceInput.messageId ? null : current
      );
    }
  };

  if (!snapshot) {
    if (loadingSession) {
      return (
        <main className="min-h-screen overflow-x-hidden bg-[#FDF8F5] px-4 py-6 text-[#4A3E39] sm:px-6 sm:py-10">
          <div className="mx-auto flex min-h-[calc(100vh-3rem)] w-full max-w-4xl items-center justify-center">
            <div className="rounded-[2rem] border border-[#EADBC8] bg-white px-6 py-5 text-sm font-bold text-[#6B5851] shadow-[0_20px_48px_rgba(124,90,78,0.10)]">
              会議を読み込んでいます...
            </div>
          </div>
        </main>
      );
    }

    return null;
  }

  const hasSummaryMessage = hasGroupTalkSummaryMessage(allTurns);
  const staleReport = isGroupTalkReportStale(report);
  const canRegenerateReport = canRegenerateGroupTalkReport(report);
  const shouldOfferReportUpdate = staleReport && canRegenerateReport;
  const canCreateReport = hasSummaryMessage || report?.status === "failed" || shouldOfferReportUpdate;
  const showReportButton =
    hasSummaryMessage || report?.status === "completed" || report?.status === "failed" || report?.status === "generating";
  const reportButtonLabel = reportLoading
    ? "論点整理（確認中）"
    : reportCreating || report?.status === "generating"
      ? "論点整理（作成中）"
      : "論点整理";
  const attachedKnowledgeItems = snapshot.session.knowledgeAttachments;

  return (
    <main className={`${getAppContentViewportHeightClass()} overflow-hidden bg-[#FDF8F5] px-4 py-6 text-[#4A3E39] sm:px-6 sm:py-10`}>
      <div className="mx-auto flex h-full w-full max-w-4xl flex-col">
        <section className="mb-4 rounded-[1.4rem] border border-[#EADBC8] bg-white/88 px-4 py-3 shadow-[0_12px_28px_rgba(124,90,78,0.10)] backdrop-blur-sm">
          <div className="flex items-center justify-center gap-3">
            <h1 className="truncate text-base font-black tracking-tight text-[#7C5A4E]">
              {snapshot.session.title}
            </h1>
          </div>
          {attachedKnowledgeItems.length > 0 ? (
            <div className="mt-2 flex flex-wrap justify-center gap-1.5" aria-label="参照資料">
              {attachedKnowledgeItems.map((item) => {
                const displayName = item.title || item.originalFilename || "ナレッジ";
                return (
                  <span
                    key={item.id}
                    className="max-w-full truncate rounded-full bg-[#F8F1EC] px-2.5 py-1 text-xs font-bold text-[#8F7468] sm:max-w-[15rem]"
                    title={`${getKnowledgeTypeLabel(item.type)}: ${displayName}`}
                  >
                    {getKnowledgeTypeLabel(item.type)}: {displayName}
                  </span>
                );
              })}
            </div>
          ) : null}
        </section>

        <section className="mt-4 flex min-h-0 flex-1 flex-col overflow-hidden rounded-[2rem] border border-[#EADBC8] bg-white px-4 py-4 shadow-[0_20px_48px_rgba(124,90,78,0.10)]">
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
            {allTurns.flatMap((turn) =>
              turn.messages.map((message) => {
                const showParticipantImage = message.speakerType !== "user" && message.phase !== "thinking";
                const messageImageSrc = message.facilitator
                  ? characterAvatarSrc
                  : message.participantId
                    ? participantImageMap.get(message.participantId)
                    : message.memberId === "mei"
                      ? getGroupTalkImageSrc("mei")
                      : undefined;
                const nextChoices = message.nextChoices ?? [];
                const selectedChoiceValue =
                  selectedChoice?.messageId === message.messageId ? selectedChoice.value : null;
                const activeChoiceInputForMessage =
                  activeChoiceInput?.messageId === message.messageId ? activeChoiceInput : null;
                const showChoiceInput =
                  activeChoiceInputForMessage !== null &&
                  message.messageId === latestChoiceMessageId &&
                  selectedChoice?.messageId !== message.messageId;
                const choiceGroupDisabled =
                  message.messageId !== latestChoiceMessageId ||
                  sending ||
                  sessionSafetyReached ||
                  operationPending ||
                  selectedChoice?.messageId === message.messageId ||
                  activeChoiceInputForMessage !== null;
                const isDirectArtifact = message.phase === "facilitator_direct";
                const messageFrameClass = isDirectArtifact
                  ? "flex w-full max-w-[84%] items-start gap-3 lg:max-w-[92%] xl:max-w-[1120px] 2xl:max-w-[1200px]"
                  : "flex max-w-[84%] items-start gap-3 lg:max-w-[68%]";

                return (
                  <React.Fragment key={message.messageId}>
                    <div className={message.speakerType === "user" ? "flex justify-end" : "flex justify-start"}>
                      <div className={messageFrameClass} data-testid={`group-talk-message-frame-${message.phase}`}>
                        {showParticipantImage ? (
                          <img
                            src={messageImageSrc ?? getGroupTalkFallbackImageSrc()}
                            alt={message.speakerName}
                            className="mt-1 h-10 w-10 shrink-0 rounded-full border border-[#EADBC8] bg-[#FFF7F1] object-cover"
                            onError={(event) => {
                              const target = event.currentTarget;
                              const fallbackSrc = getGroupTalkFallbackImageSrc();
                              if (target.getAttribute("src") === fallbackSrc) return;
                              target.src = fallbackSrc;
                            }}
                          />
                        ) : null}
                        <div
                          className={`min-w-0 rounded-[1.6rem] px-4 py-3 ${isDirectArtifact ? "w-full" : ""} ${
                            message.speakerType === "user"
                              ? "bg-[#5D4D46] text-white"
                              : message.facilitator
                                ? "border border-[#E8CDBE] bg-[#FFF1E8] text-[#4A3E39]"
                                : "border border-[#EADBC8] bg-[#FFFBF8] text-[#4A3E39]"
                          }`}
                          data-testid={`group-talk-message-bubble-${message.phase}`}
                        >
                          <p className={`text-xs font-black ${message.speakerType === "user" ? "text-[#F8EDE7]" : "text-[#A67C52]"}`}>
                            {buildMessageSpeakerLabel(message, characterName, preferredCallName)}
                          </p>
                          {message.phase === "facilitator_direct" ? (
                            <div className="mt-2">
                              <SafeMarkdown text={message.content} tableVariant="artifact" />
                            </div>
                          ) : (
                            <p className="mt-2 whitespace-pre-wrap text-sm leading-7">{message.content}</p>
                          )}
                        </div>
                      </div>
                    </div>
                    {nextChoices.length > 0 ? (
                      <div className="ml-12 flex justify-start">
                        <div
                          className={`w-full max-w-[84%] rounded-[1.4rem] border border-[#EADBC8] bg-[#FFFDFB] px-3 py-3 shadow-sm lg:max-w-[68%] ${
                            choiceGroupDisabled ? "opacity-75" : ""
                          }`}
                        >
                          <p className="px-1 text-xs font-bold text-[#9C867D]">次に進める方向を選んでください</p>
                          <div className="mt-2 flex flex-col gap-2">
                            {nextChoices.map((choice, choiceIndex) => {
                              const isSelected = selectedChoiceValue === choice.value;
                              const isFinishChoice = choice.choiceType === "finish";
                              const disabled = choiceGroupDisabled || isFinishChoice;
                              return (
                                <button
                                  key={`${message.messageId}-${choice.value}-${choiceIndex}`}
                                  type="button"
                                  disabled={disabled}
                                  onClick={() => void handleChoiceSelect(message, choice)}
                                  className={`min-h-[44px] rounded-[1rem] border px-3 py-2 text-left text-sm font-bold leading-6 transition ${
                                    isSelected
                                      ? "border-[#A67C52] bg-[#F8EBDD] text-[#5D4D46]"
                                      : "border-[#EADBC8] bg-white text-[#5D4D46] hover:border-[#CFAE9B] hover:bg-[#FFF7F1]"
                                  } disabled:cursor-not-allowed disabled:hover:border-[#EADBC8] disabled:hover:bg-white`}
                                >
                                  <span className="flex flex-wrap items-center gap-2">
                                    {isSelected ? (
                                      <span className="rounded-full bg-[#A67C52] px-2 py-0.5 text-[11px] font-black text-white">
                                        選択済み
                                      </span>
                                    ) : isFinishChoice ? (
                                      <span className="rounded-full bg-[#ECE4DD] px-2 py-0.5 text-[11px] font-black text-[#8B7A72]">
                                        終了
                                      </span>
                                    ) : choice.isRecommended ? (
                                      <span className="rounded-full bg-[#F4DED0] px-2 py-0.5 text-[11px] font-black text-[#8A5D4F]">
                                        おすすめ
                                      </span>
                                    ) : null}
                                    <span>{choice.label}</span>
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                          {showChoiceInput ? (
                            <form
                              className="mt-3 rounded-[1rem] border border-[#EADBC8] bg-[#FFF8F2] px-3 py-3"
                              onSubmit={(event) => void handleChoiceInputSubmit(event)}
                            >
                              <label
                                htmlFor={`choice-input-${message.messageId}`}
                                className="block text-xs font-bold text-[#7C5A4E]"
                              >
                                {getChoiceInputPrompt(activeChoiceInputForMessage.choice)}
                              </label>
                              <textarea
                                id={`choice-input-${message.messageId}`}
                                value={activeChoiceInputForMessage.value}
                                onChange={(event) => handleChoiceInputChange(message.messageId, event.currentTarget.value)}
                                placeholder={activeChoiceInputForMessage.choice.inputPlaceholder || undefined}
                                maxLength={GROUP_TALK_CHOICE_INPUT_MAX_LENGTH}
                                rows={3}
                                disabled={sending || sessionSafetyReached || operationPending}
                                className="mt-2 min-h-[88px] w-full resize-none rounded-[0.9rem] border border-[#EADBC8] bg-white px-3 py-2 text-sm leading-6 text-[#4A3E39] shadow-sm focus:outline-none focus:ring-2 focus:ring-[#D1907B]/20 disabled:cursor-not-allowed disabled:opacity-70"
                              />
                              <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                <span className="text-xs font-bold text-[#9C867D]">
                                  {activeChoiceInputForMessage.value.length}/{GROUP_TALK_CHOICE_INPUT_MAX_LENGTH}
                                </span>
                                <div className="flex gap-2">
                                  <button
                                    type="button"
                                    onClick={() => handleChoiceInputCancel(message.messageId)}
                                    disabled={sending || sessionSafetyReached || operationPending}
                                    className="min-h-[40px] rounded-[0.9rem] border border-[#EADBC8] bg-white px-3 py-2 text-sm font-bold text-[#7C5A4E] transition hover:border-[#CFAE9B] hover:bg-[#FFF7F1] disabled:cursor-not-allowed disabled:opacity-60"
                                  >
                                    キャンセル
                                  </button>
                                  <button
                                    type="submit"
                                    disabled={sending || sessionSafetyReached || operationPending || !activeChoiceInputForMessage.value.trim()}
                                    className="min-h-[40px] rounded-[0.9rem] bg-[#5D4D46] px-4 py-2 text-sm font-black text-white transition hover:bg-[#4A3E39] disabled:cursor-not-allowed disabled:opacity-60"
                                  >
                                    {sending ? "送信中..." : "送信"}
                                  </button>
                                </div>
                              </div>
                              {activeChoiceInputForMessage.error ? (
                                <p role="alert" className="mt-2 text-xs font-bold text-[#C06D5A]">
                                  {activeChoiceInputForMessage.error}
                                </p>
                              ) : null}
                            </form>
                          ) : null}
                        </div>
                      </div>
                    ) : null}
                  </React.Fragment>
                );
              })
            )}
            <div ref={endRef} />
          </div>

          {recovering ? (
            <p role="status" className="mt-4 text-sm font-bold text-[#7C5A4E]">
              送信結果を確認しています…
            </p>
          ) : null}

          {loadError ? (
            sessionSafetyReached ? (
              <div role="alert" className="mt-4 rounded-[1rem] border border-[#E8CDBE] bg-[#FFF7F1] px-4 py-3">
                <p className="text-sm font-bold text-[#7C5A4E]">{loadError}</p>
                <Link
                  to="/talk/group"
                  className="mt-3 inline-flex min-h-[40px] items-center rounded-[0.9rem] bg-[#5D4D46] px-4 py-2 text-sm font-black text-white transition hover:bg-[#4A3E39]"
                >
                  新しい壁打ちを始める
                </Link>
              </div>
            ) : (
              <p role="alert" className="mt-4 text-sm font-bold text-[#C06D5A]">
                {loadError}
              </p>
            )
          ) : null}

          <div ref={composerRef} className="mt-4 flex flex-col gap-3 border-t border-[#F0E7DF] pt-4">
            <div className="flex min-w-0 flex-1 items-end gap-3">
              <textarea
                value={composerText}
                onChange={(event) => setComposerText(event.target.value)}
                placeholder={hasActiveChoices ? "選択肢を選ぶか、自由に入力してください" : "自由に入力してください"}
                rows={3}
                disabled={sending || sessionSafetyReached || operationPending}
                aria-invalid={composerOverLimit}
                className={`min-h-[84px] flex-1 resize-none rounded-[1.4rem] border bg-[#FFFDFB] px-4 py-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#D1907B]/20 ${composerOverLimit ? "border-[#D1907B]" : "border-[#EADBC8]"}`}
              />
              <button
                type="button"
                onClick={() => void handleSend()}
                disabled={sending || sessionSafetyReached || operationPending || !composerText.trim() || composerOverLimit}
                className="shrink-0 rounded-[1.4rem] bg-[#5D4D46] px-5 py-3 text-sm font-black text-white transition hover:bg-[#4A3E39] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {recovering ? "確認中..." : sending ? "送信中..." : "送信"}
              </button>
            </div>
            <div
              data-testid="group-talk-composer-meta"
              className={`flex items-center gap-3 ${showReportButton ? "justify-between" : "justify-end"}`}
            >
              {showReportButton ? (
                <button
                  type="button"
                  data-testid="group-talk-report-trigger"
                  onClick={() => setReportModalOpen(true)}
                  disabled={reportLoading || reportCreating}
                  className="min-h-[36px] w-auto shrink-0 rounded-full border border-[#A67C52] bg-white px-3 py-1.5 text-xs font-black text-[#7C5A4E] transition hover:bg-[#FFF7F1] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {reportButtonLabel}
                </button>
              ) : null}
              <p className={`text-right text-xs font-bold ${composerOverLimit ? "text-[#C06D5A]" : "text-[#8F7468]"}`}>
                {composerCharacterCount.toLocaleString()} / {groupTalkPolicy ? groupTalkPolicy.userInputMaxCharacters.toLocaleString() : "-"}文字
              </p>
            </div>
            {composerOverLimit && groupTalkPolicy ? (
              <div role="alert" className="rounded-[1rem] border border-[#F0C8BA] bg-[#FFF7F1] px-4 py-3 text-sm font-bold leading-6 text-[#C06D5A]">
                <p>{buildGroupTalkOverLimitMessage(groupTalkPolicy)}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Link to="/talk/group" className="rounded-full bg-[#5D4D46] px-4 py-2 text-xs font-black text-white">
                    新しい壁打ちを始める
                  </Link>
                  {groupTalkPolicy.knowledgeReusableFromLibrary ? (
                    <Link to="/request/knowledge" className="rounded-full border border-[#D1907B] bg-white px-4 py-2 text-xs font-black text-[#A65F4E]">
                      ナレッジライブラリを見る
                    </Link>
                  ) : null}
                </div>
              </div>
            ) : null}
            {catalogError ? (
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-[1rem] border border-[#EADBC8] bg-[#FFFDFB] px-4 py-3 text-xs font-bold text-[#8F7468]">
                <span>利用条件を取得できませんでした。送信時はサーバー側でも確認されます。</span>
                <button type="button" onClick={retryCatalog} className="rounded-full border border-[#D9C5B9] bg-white px-3 py-1.5 text-[#6B5851]">
                  再試行
                </button>
              </div>
            ) : null}
          </div>
        </section>
        <GroupTalkReportModal
          isOpen={reportModalOpen}
          report={report}
          loading={reportLoading}
          creating={reportCreating}
          errorMessage={reportError}
          canCreate={canCreateReport}
          onCreate={() => void handleGenerateReport()}
          onClose={() => setReportModalOpen(false)}
        />
      </div>
    </main>
  );
}
